import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FiMapPin, FiClock, FiCalendar, FiNavigation, FiPhone, FiMail } from 'react-icons/fi';
import './WeddingDetails.css';

const WeddingDetails = () => {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Countdown timer
  React.useEffect(() => {
    const calculateCountdown = () => {
      const weddingDate = new Date('October 19, 2025 09:15:00').getTime();
      const now = new Date().getTime();
      const distance = weddingDate - now;

      if (distance > 0) {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    };

    calculateCountdown();
    const timer = setInterval(calculateCountdown, 1000);
    return () => clearInterval(timer);
  }, []);

  const weddingEvents = [
    {
      id: 1,
      title: 'Wedding Ceremony (Mohoortham)',
      time: '9:15 AM - 9:45 AM',
      date: 'October 19, 2025',
      location: 'Aiswarya Auditorium, Nilambur',
      address: '76FH+V5C, V.K. Rd, Manalody, Nilambur, Kerala 679329',
      description: 'The sacred Hindu wedding ceremony with Vedic rituals, exchange of garlands, and seven sacred vows around the holy fire during the auspicious Mohoortham time.',
      icon: '🕉️',
      color: '#DC143C',
      mapLink: 'https://maps.app.goo.gl/zJgTHgpNgNkWqi3X7',
      isEmoji: true
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const countdownVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="details" className="wedding-details-section" ref={ref}>
      <div className="details-background">
        <div className="bg-shapes">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className={`shape shape-${i + 1}`}
              animate={{
                y: [-20, 20, -20],
                x: [-10, 10, -10],
                rotate: [0, 180, 360]
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                delay: i * 1.5,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
      </div>

      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="calendar-decoration"
            animate={{
              rotate: [0, 5, -5, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <FiCalendar />
          </motion.div>
          <h2>Wedding Details</h2>
          <p>Join us in celebrating our special moments</p>
        </motion.div>

        {/* Countdown Timer */}
        <motion.div
          className="countdown-container"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h3>Countdown to Our Big Day</h3>
          <div className="countdown-grid">
            {Object.entries(countdown).map(([unit, value], index) => (
              <motion.div
                key={unit}
                className="countdown-item"
                variants={countdownVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ delay: 0.5 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <motion.div
                  className="countdown-number"
                  key={value}
                  initial={{ scale: 1.2, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {value.toString().padStart(2, '0')}
                </motion.div>
                <div className="countdown-label">{unit}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Wedding Events */}
        <motion.div
          className="events-container"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="events-grid">
            {weddingEvents.map((event, index) => (
              <motion.div
                key={event.id}
                className="event-card"
                variants={cardVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="event-header">
                  <motion.div
                    className="event-icon"
                    style={{ backgroundColor: event.isEmoji ? 'transparent' : event.color }}
                    whileHover={{ 
                      rotate: 360,
                      scale: 1.1
                    }}
                    transition={{ duration: 0.6 }}
                  >
                    {event.isEmoji ? (
                      <span className="event-emoji" style={{ color: event.color }}>
                        {event.icon}
                      </span>
                    ) : (
                      <event.icon />
                    )}
                  </motion.div>
                  <div className="event-title-section">
                    <h3>{event.title}</h3>
                    <div className="event-datetime">
                      <span className="event-date">
                        <FiCalendar />
                        {event.date}
                      </span>
                      <span className="event-time">
                        <FiClock />
                        {event.time}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="event-content">
                  <div className="event-location">
                    <FiMapPin className="location-icon" />
                    <div>
                      <h4>{event.location}</h4>
                      <p>{event.address}</p>
                    </div>
                  </div>
                  
                  <p className="event-description">{event.description}</p>
                  
                  <div className="event-actions">
                    <motion.a
                      href={event.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="action-btn primary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FiNavigation />
                      Get Directions
                    </motion.a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default WeddingDetails;
